import React, { useEffect } from 'react';
import { usePlatformConfig } from '../../contexts/PlatformConfigContext';
import { useTerminology } from '../../hooks/useTerminology';
interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: React.ReactNode;
  /**
   * Set the browser title from this page. Default on, because the page heading
   * is what the tab should say. Pass `false` where a page owns its own title.
   */
  documentTitle?: boolean;
}

/**
 * Every page heading passes through terminology resolution, so a title written
 * as `'{housing} management'` follows the deployment's vocabulary — and the
 * browser title follows the heading rather than being written a second time in
 * a different file, where the two would drift apart.
 *
 * Plain text passes through untouched, so no existing page needed editing.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  documentTitle = true
}: PageHeaderProps) {
  const {
    phrase
  } = useTerminology();
  const {
    config
  } = usePlatformConfig();
  const resolvedTitle = phrase(title);
  const resolvedEyebrow = eyebrow ? phrase(eyebrow) : undefined;
  const resolvedDescription = description ? phrase(description) : undefined;
  useEffect(() => {
    if (!documentTitle) return;
    const previous = document.title;
    const platform = config.platform.shortName || config.platform.name;
    document.title = platform ? `${resolvedTitle} · ${platform}` : resolvedTitle;
    return () => {
      document.title = previous;
    };
  }, [documentTitle, resolvedTitle, config.platform.name, config.platform.shortName]);
  return <div className="flex flex-col gap-3 border-b border-line pb-5 sm:flex-row sm:items-end sm:justify-between" data-id="element-5907" data-mp="o0652bj2vhvg">
      <div className="min-w-0" data-id="element-5908" data-mp="j1cxt0udwn3q">
        {resolvedEyebrow && <p className="text-body-sm font-medium text-ink-muted" data-id="element-5909" data-mp="1gi216yyzfxf8">{resolvedEyebrow}</p>}
        <h1 className="mt-1 text-h2 font-semibold tracking-tight text-ink lg:text-h1" data-id="element-5910" data-mp="1bqa7ifkj5bkv">
          {resolvedTitle}
        </h1>
        {resolvedDescription && <p className="mt-2.5 max-w-[62ch] text-body-lg text-ink-muted" data-id="element-5911" data-mp="360qqu56fkpcu">{resolvedDescription}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2 sm:shrink-0" data-id="element-5912" data-mp="2w946njc47fxy">{actions}</div>}
    </div>;
}